/**
 * Filtro de exibição por faixa de valor de pixel.
 *
 * Quem decide se uma camada aceita o filtro é o próprio colormap do SLD: se o
 * GeoServer devolve uma rampa contínua (ver useRasterColormap), o controle
 * aparece e os limites saem de lá. Máscaras categóricas — as camadas
 * `potencial_*` de pepe-cog, com colormap `values` de 0/255 — e rasters sem
 * colormap ficam de fora, porque "faixa de valores" não significa nada nelas.
 *
 * O registro abaixo é só o extra que não dá para inferir do colormap.
 */
export interface RasterFilterMeta {
  /** Chave da camada em /api/v1/tiles/{key}/{z}/{x}/{y}.png. Sem ela o
   *  backend não sabe servir esse raster, então o recorte em cor sólida não é
   *  oferecido e sobra apenas o recorte com o gradiente original. */
  tileKey?: string;
  unit?: string;
  /** Aviso exibido quando o recorte em cor sólida está ligado. Serve para as
   *  camadas com dimensão (elevação/tempo): o endpoint de tiles serve uma
   *  fatia fixa, então a cor sólida ignora a seleção do DimensionControl. */
  tileNote?: string;
  /** Valor de referência oferecido como atalho no controle. */
  reference?: { value: number; hint: string };
}

export const RASTER_FILTER_META: Record<string, RasterFilterMeta> = {
  solar_ghi_200m_sirgas2000_anual: {
    tileKey: "ghi",
    unit: "kWh/m²/dia",
    reference: {
      value: 5.8,
      hint: "Potencial solar técnico exclui áreas com GHI < 5,8",
    },
  },
  windspeedtest: {
    tileKey: "vento",
    unit: "m/s",
    // O backend cacheia essa camada na fatia de 50 m (wcs_subset
    // Elevation(50.0) em tile_controller.py), altura de referência da
    // extrapolação de Hellman no cálculo de potencial. A cor sólida não
    // acompanha a elevação escolhida; o gradiente, sim.
    tileNote: "A cor sólida usa sempre a fatia de 50 m.",
    reference: {
      value: 6,
      hint: "Potencial eólico exclui velocidade < 6 m/s",
    },
  },
  DENSIDADE_POTENCIA_100m: { unit: "W/m²" },
  MOSAICO_100m_COG: { unit: "m" },
};

export type RasterRangeOperator = "maior" | "menor" | "entre";

export interface RasterValueRange {
  /** Verdadeiro pinta o recorte numa cor sólida (tiles do backend); falso
   *  mantém as cores do gradiente original (SLD dinâmico no GeoServer). Nos
   *  dois casos o recorte é o mesmo. */
  solidColor: boolean;
  operator: RasterRangeOperator;
  value: number;
  valueMax: number;
}

/** Converte a faixa escolhida no controle nos parâmetros do endpoint de tiles. */
export function rangeToTileParams(range: RasterValueRange): {
  minVal?: number;
  maxVal?: number;
} {
  switch (range.operator) {
    case "maior":
      return { minVal: range.value };
    case "menor":
      return { maxVal: range.value };
    case "entre":
      return { minVal: range.value, maxVal: range.valueMax };
  }
}

/**
 * Passo do controle a partir da amplitude real da camada: GHI varia ~2 e pede
 * centésimos, o MDT varia 4.000 e pediria dezenas. Uma constante única serviria
 * mal aos dois.
 */
export function stepForRange(min: number, max: number): number {
  const span = Math.abs(max - min);
  if (!(span > 0)) return 1;
  return Math.pow(10, Math.floor(Math.log10(span / 100)));
}
